import { useContext, useEffect, useState } from 'react';
import { AccountContext } from '../AccountContext';
import { Error } from '@app/shared';
import { isCancelled } from './RegularPaymentHelpers';

function shouldCreate(regularPayment: any): boolean {
  if (regularPayment && !isCancelled(regularPayment)) {
    return false;
  }
  return true;
}

export function CreateOrUpdateRegularPayment(props: { regularPayment: any; onSubmit: any }) {
  const regularPayment = props.regularPayment;
  const { error, setError, accountId, regularPaymentService } = useContext(AccountContext);
  const [submitting, setSubmitting] = useState(false as boolean);
  const [sortCode, setSortCode] = useState(undefined as string);
  const [bankAccountNumber, setBankAccountNumber] = useState(undefined as string);
  const [paymentType, setPaymentType] = useState(undefined as string);
  const [fixedAmount, setFixedAmount] = useState(undefined as string);
  const [percentageAmount, setPercentageAmount] = useState(undefined as string);
  const [collectionDateAdjustmentDays, setCollectionDateAdjustmentDays] = useState(undefined as string | null);
  const [delayFirstCollectionToNextPeriod, setDelayFirstCollectionToNextPeriod] = useState(false as boolean);

  useEffect(() => {
    const regularPayment = props.regularPayment?.planned ?? props.regularPayment?.current;
    setSortCode(regularPayment?.sortCode);
    setBankAccountNumber(regularPayment?.bankAccountNumber);
    setPaymentType(regularPayment?.paymentType);
    setFixedAmount(regularPayment?.fixedAmount?.value);
    setPercentageAmount(regularPayment?.percentageAmount);
    setCollectionDateAdjustmentDays(regularPayment?.collectionDateAdjustmentDays);
  }, [props.regularPayment]);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setSubmitting(true);

      if (shouldCreate(regularPayment)) {
        await regularPaymentService.createRegularPayment(
          accountId,
          bankAccountNumber,
          sortCode,
          paymentType,
          fixedAmount,
          percentageAmount,
          collectionDateAdjustmentDays,
          delayFirstCollectionToNextPeriod
        );
      } else {
        await regularPaymentService.updateRegularPayment(
          accountId,
          bankAccountNumber,
          sortCode,
          paymentType,
          fixedAmount,
          percentageAmount,
          collectionDateAdjustmentDays
        );
      }

      props.onSubmit();
    } catch (error) {
      setError({ source: 'Create Regular Payment', ...error });
    } finally {
      setSubmitting(false);
    }
  };

  const requiresFixedAmountField = paymentType === 'FixedAmount' || paymentType === 'FixedDDPlus';
  return (
    <>
      <section>
        <h4>Regular Payment</h4>
        <Error error={error} />
        <form className="ui card" onSubmit={(e) => handleSubmit(e)}>
          <div className="content">
            <div className="ui form">
              <div className="field">
                <label>Sort Code</label>
                <input
                  type="text"
                  value={sortCode || ''}
                  onChange={(e) => setSortCode(e.target.value)}
                  disabled={submitting}
                />
                {sortCode && sortCode.length != 6 && (
                  <div className="ui pointing red basic label">Sort code must be 6 digits</div>
                )}
              </div>
              <div className="field">
                <label>Bank Account Number</label>
                <input
                  type="text"
                  value={bankAccountNumber || ''}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  disabled={submitting}
                />
                {bankAccountNumber && bankAccountNumber.length != 8 && (
                  <div className="ui pointing red basic label">Bank account number must be 8 digits</div>
                )}
              </div>
              <div className="field">
                <label>Payment Type</label>
                <select onChange={(e) => setPaymentType(e.target.value)} value={paymentType}>
                  <option>--Select a payment type--</option>
                  <option value="PaymentRequested">Payment Requested</option>
                  <option value="FixedAmount">Fixed Amount</option>
                  <option value="FullStatementBalance">Full Statement Balance</option>
                  <option value="PercentageAmount">Percentage Amount</option>
                  <option value="FullRevolvingBalanceAndInstalmentDue">
                    Full Revolving Balance And Instalment Due
                  </option>
                  <option value="ContractualMinimum">Contractual Minimum</option>
                  <option value="BoostedPayment">Boosted Payment</option>
                  <option value="FixedDDPlus">Fixed DD Plus</option>
                </select>
              </div>
              {requiresFixedAmountField && (
                <div className="field">
                  <label>Fixed Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={fixedAmount || ''}
                    onChange={(e) => setFixedAmount(e.target.value)}
                    disabled={!paymentType || submitting}
                  />
                  {requiresFixedAmountField && +fixedAmount <= 0 && (
                    <div className="ui pointing red basic label">Fixed amount must be greater than 0</div>
                  )}
                </div>
              )}
              {paymentType == 'PercentageAmount' && (
                <div className="field">
                  <label>Percentage Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={percentageAmount || ''}
                    onChange={(e) => setPercentageAmount(e.target.value)}
                    disabled={!paymentType || submitting}
                  />
                  {paymentType == 'PercentageAmount' && +percentageAmount <= 0 && (
                    <div className="ui pointing red basic label">Percentage amount must be greater than 0</div>
                  )}
                </div>
              )}
              <fieldset>
                <legend>Collection Options:</legend>
                <div className="field">
                  <label>Adjustment Days</label>
                  <input
                    type="text"
                    value={collectionDateAdjustmentDays || null}
                    onChange={(e) => setCollectionDateAdjustmentDays(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                {!regularPayment && (
                  <div className="field">
                    <div className="ui labeled input">
                      <div className="ui checkbox">
                        <input
                          type="checkbox"
                          id={'nxtCollection'}
                          checked={delayFirstCollectionToNextPeriod || false}
                          onChange={(e) => setDelayFirstCollectionToNextPeriod(!delayFirstCollectionToNextPeriod)}
                          disabled={submitting}
                        />
                        <label htmlFor={'nxtCollection'}>Delay First Collection</label>
                      </div>
                    </div>
                  </div>
                )}
              </fieldset>
            </div>
          </div>
          <button
            className={`ui button ${submitting && 'loading'}`}
            type="submit"
            disabled={
              sortCode?.length != 6 ||
              bankAccountNumber?.length != 8 ||
              [
                'FullStatementBalance',
                'FixedAmount',
                'PaymentRequested',
                'PercentageAmount',
                'FullRevolvingBalanceAndInstalmentDue',
                'ContractualMinimum',
                'BoostedPayment',
                'FixedDDPlus',
              ].indexOf(paymentType) == -1 ||
              (requiresFixedAmountField && (+fixedAmount <= 0 || !fixedAmount)) ||
              (paymentType == 'PercentageAmount' && (+percentageAmount <= 0 || !percentageAmount))
            }>
            <span>{shouldCreate(regularPayment) ? 'Create' : 'Update'}</span>
          </button>
        </form>
      </section>
    </>
  );
}
